import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Package, Truck, CheckCircle, XCircle, Loader2, AlertTriangle, RefreshCw } from "lucide-react";

interface ShippingStatusProps {
  status: string | null;
  trackingCode?: string | null;
  labelUrl?: string | null;
  provider?: string;
  shipmentId?: string;
  onCancel?: (shipmentId: string) => void;
  isCancelling?: boolean;
  onRetryPrint?: (shipmentId: string) => void;
  isRetryingPrint?: boolean;
  onViewLabel?: (shipmentId: string) => void;
  isFetchingLabel?: boolean;
}

const STATUS_CONFIG: Record<string, { 
  label: string; 
  variant: "default" | "secondary" | "outline"; 
  icon: typeof Package;
  className?: string;
}> = {
  pending: { 
    label: "Aguardando envio", 
    variant: "secondary", 
    icon: Package 
  },
  label_generated: {
    label: "Etiqueta gerada",
    variant: "outline",
    icon: Package,
    className: "border-blue-500 text-blue-600"
  },
  label_pending: {
    label: "Etiqueta pendente",
    variant: "outline",
    icon: AlertTriangle,
    className: "border-amber-500 text-amber-600"
  },
  shipped: { 
    label: "Enviado", 
    variant: "default", 
    icon: Truck,
    className: "bg-primary"
  },
  in_transit: { 
    label: "Em trânsito", 
    variant: "default", 
    icon: Truck,
    className: "bg-secondary"
  },
  delivered: {
    label: "Entregue",
    variant: "default",
    icon: CheckCircle,
    className: "bg-primary"
  },
  cancelled: {
    label: "Cancelado",
    variant: "outline",
    icon: XCircle,
    className: "border-red-500 text-red-600"
  },
};

function getTrackingUrl(trackingCode: string, provider?: string): string {
  if (provider === "correios") {
    return `https://rastreamento.correios.com.br/app/index.php?objeto=${trackingCode}`;
  }
  if (provider === "manda_bem") {
    return `https://mandabem.com.br/rastreamento/${trackingCode}`;
  }
  if (provider === "super_frete") {
    return `https://rastreamento.correios.com.br/app/index.php?objeto=${trackingCode}`;
  }
  return `https://www.melhorrastreio.com.br/rastreio/${trackingCode}`;
}

export function ShippingStatus({ status, trackingCode, labelUrl, provider, shipmentId, onCancel, isCancelling, onRetryPrint, isRetryingPrint, onViewLabel, isFetchingLabel }: ShippingStatusProps) {
  if (!status) return null;

  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  const canCancelSuperFrete = provider === "super_frete" && shipmentId && onCancel
    && (status === "label_generated" || status === "label_pending");
  const canRetryPrintSuperFrete = provider === "super_frete" && shipmentId && onRetryPrint
    && status === "label_pending";
  const isSuperFreteWithFreshFetch = provider === "super_frete" && shipmentId && onViewLabel
    && (status === "label_generated" || status === "label_pending");
  const showStaticLabelButton = !!labelUrl && !isSuperFreteWithFreshFetch;
  const retryButtonLabel = "Gerar etiqueta";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Badge variant={config.variant} className={config.className}>
          <Icon className="mr-1 h-3 w-3" />
          {config.label}
        </Badge>
        {provider && (
          <span className="text-[10px] text-muted-foreground">
            via {provider === "correios" ? "Correios" : provider === "manda_bem" ? "Manda Bem" : provider === "super_frete" ? "SuperFrete" : "Melhor Envio"}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {trackingCode && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              window.open(getTrackingUrl(trackingCode, provider), "_blank");
            }}
          >
            <ExternalLink className="mr-1 h-3 w-3" />
            Rastrear: {trackingCode}
          </Button>
        )}

        {showStaticLabelButton && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(labelUrl!, "_blank")}
          >
            <ExternalLink className="mr-1 h-3 w-3" />
            Ver Etiqueta
          </Button>
        )}

        {isSuperFreteWithFreshFetch && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewLabel!(shipmentId!)}
            disabled={isFetchingLabel}
          >
            {isFetchingLabel ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <ExternalLink className="mr-1 h-3 w-3" />
            )}
            Ver Etiqueta
          </Button>
        )}

        {canRetryPrintSuperFrete && (
          <Button
            variant="outline"
            size="sm"
            className={status === "label_pending"
              ? "text-amber-700 border-amber-300 hover:bg-amber-50"
              : "text-muted-foreground"}
            onClick={() => onRetryPrint!(shipmentId!)}
            disabled={isRetryingPrint}
          >
            {isRetryingPrint ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="mr-1 h-3 w-3" />
            )}
            {retryButtonLabel}
          </Button>
        )}

        {canCancelSuperFrete && (
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 border-red-300 hover:bg-red-50"
            onClick={() => onCancel!(shipmentId!)}
            disabled={isCancelling}
          >
            {isCancelling ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <XCircle className="mr-1 h-3 w-3" />
            )}
            Cancelar Envio
          </Button>
        )}
      </div>
    </div>
  );
}
